import { FC, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
} from "@material-ui/icons";

import { Button, ErrorMessage, Modal, TermsAndConditions } from "components";
import { useAssetDetailData } from "containers/AssetDetail/hooks";
import { formatCurrencyAmount } from "lib/format";
import { parseNumber } from "lib/parse";
import { DEFAULT_ERROR_MESSAGE } from "services/constants";
import { BaseAmountSource } from "services/listings/types";
import { createProxy, deleteProxy } from "services/proxies";
import theme from "theme";
import { circleIconSize } from "theme/constants";

import CreateEditProxyForm from "./CreateEditProxyForm";
interface CreateEditProxyModalProps {
  onClose: () => void;
  marketplaceMedianSource: BaseAmountSource;
  marketplaceMedianMultiplier: number;
  marketplaceMedian: string | null;
  buyNowPrice: number;
  highestOpenBuyerOfferAmount: number;
  isMarketplaceMedianDisabled: boolean;
  listingId: number;
  listingProxyIncrementAmount: number;
  ltm: number;
}

const CreateEditProxyModal: FC<CreateEditProxyModalProps> = ({
  onClose,
  marketplaceMedianSource,
  marketplaceMedianMultiplier,
  marketplaceMedian,
  buyNowPrice,
  highestOpenBuyerOfferAmount,
  isMarketplaceMedianDisabled,
  listingId,
  listingProxyIncrementAmount,
  ltm,
}) => {
  const { proxy, handleRefreshProxy } = useAssetDetailData(listingId);
  const initialEditProxyOfferValues = {
    amount: proxy?.amount,
  };

  const titleByStep = [
    !!proxy ? "Edit Proxy" : "Create Proxy",
    "Confirm Proxy",
    !!proxy ? "Proxy Edited" : "Proxy Created",
    "Proxy Closed",
  ];

  const [step, setStep] = useState(0);
  const [values, setValues] = useState(initialEditProxyOfferValues);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const makeOnBackHandler = () => {
    if (step === 1) {
      return () => {
        setStep(0);
      };
    } else {
      return undefined;
    }
  };

  const handleClose = () => {
    handleRefreshProxy();
    onClose();
  };

  const handleCreateProxy = async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      await createProxy({
        listing_id: listingId,
        amount: parseNumber(values.amount, 2),
      });
      handleRefreshProxy();
      setStep(s => s + 1);
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : DEFAULT_ERROR_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseProxy = async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      await deleteProxy(proxy.id);
      setStep(3);
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : DEFAULT_ERROR_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title={titleByStep[step]}
      open
      onClose={handleClose}
      onBack={makeOnBackHandler()}
    >
      {step === 0 && (
        <>
          <CreateEditProxyForm
            onSave={async v => {
              setValues(v);
              setStep(s => s + 1);
            }}
            initialValues={values}
            isLoading={isLoading}
            marketplaceMedianSource={marketplaceMedianSource}
            marketplaceMedianMultiplier={marketplaceMedianMultiplier}
            marketplaceMedian={marketplaceMedian}
            buyNowPrice={buyNowPrice}
            highestOpenBuyerOfferAmount={highestOpenBuyerOfferAmount}
            isMarketplaceMedianDisabled={isMarketplaceMedianDisabled}
            listingProxyIncrementAmount={listingProxyIncrementAmount}
            ltm={ltm}
            proxy={proxy}
          />
          {!!proxy && (
            <Box textAlign="center">
              <Button
                customColor="red"
                size="large"
                variant="contained"
                fullWidth
                onClick={handleCloseProxy}
                id="withdraw"
                disabled={isLoading}
                isLoading={isLoading}
              >
                Close Proxy
              </Button>
            </Box>
          )}
        </>
      )}
      {step === 1 && (
        <>
          <Typography component="div" gutterBottom>
            <Box fontSize={13} textAlign="center">
              {`Your proxy will immediately be ${
                !!proxy ? "edited" : "created"
              }`}
            </Box>
          </Typography>
          <Box m={2} textAlign="center">
            {errorMessage && (
              <Box my={2}>
                <ErrorMessage
                  title="Request Failed"
                  description={errorMessage}
                />
              </Box>
            )}
            <Box fontSize={18} m={1}>
              Your Proxy Amount
            </Box>
            <Box
              fontSize={18}
              fontWeight={theme.typography.fontWeightMedium}
              m={1}
            >
              {formatCurrencyAmount("USD", parseNumber(values.amount, 2))}
            </Box>
          </Box>
          <Box m={2} textAlign="center">
            <TermsAndConditions />
            <Box m={2}>
              <Button
                customColor="green"
                variant="contained"
                size="large"
                fullWidth
                onClick={handleCreateProxy}
                id="confirm-proxy"
                disabled={isLoading}
                isLoading={isLoading}
              >
                Confirm Proxy
              </Button>
            </Box>
          </Box>
        </>
      )}
      {step === 2 && (
        <>
          <Typography component="div">
            <Box textAlign="center" mb={2}>
              <CheckCircleIcon
                style={{
                  fontSize: circleIconSize.big,
                  color: theme.custom.palette.green.main,
                }}
              />
            </Box>
            <Box fontSize={15} textAlign="center" m={2}>
              Your proxy has been successfully edited.
            </Box>
          </Typography>
        </>
      )}
      {step === 3 && (
        <>
          <Typography component="div">
            <Box textAlign="center" mb={2}>
              <CancelIcon
                style={{
                  fontSize: circleIconSize.big,
                  color: theme.custom.palette.red.main,
                }}
              />
            </Box>
            <Box fontSize={15} textAlign="center" m={2}>
              Your proxy has been successfully closed.
            </Box>
          </Typography>
        </>
      )}
    </Modal>
  );
};

export default CreateEditProxyModal;
